import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronUp, X } from "lucide-react";
import { groupSelected, formatRupiah, styleOf } from "@/lib/dirga";
import { AnimatedRupiah } from "@/components/dirga/AnimatedRupiah";
import { cn } from "@/lib/utils";

const SummaryLines = ({ selectedIds, styles = {}, onRemove }) => {
  const groups = groupSelected(selectedIds);
  const categories = Object.keys(groups);

  if (categories.length === 0) {
    return (
      <p className="font-sans text-sm leading-relaxed text-[#8A8A8A]">
        Belum ada yang dipilih. Pilih minimal satu layanan untuk mulai menyusun paketmu.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {categories.map((cat) => (
        <div key={cat}>
          <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#8C6D32]">
            {cat}
          </p>
          <ul className="mt-3 space-y-3">
            {groups[cat].map((item) => {
              const st = styleOf(item.id, styles[item.id]);
              return (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3"
                  data-testid={`summary-line-${item.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-sans text-sm text-[#1C1C1C]">{item.name}</p>
                    {st && (
                      <p className="font-sans text-xs text-[#5C5C5C]">Gaya: {st.name}</p>
                    )}
                    <p className="font-sans text-xs text-[#8A8A8A]">
                      {formatRupiah(item.price)}
                    </p>
                  </div>
                  {onRemove && (
                    <button
                      type="button"
                      aria-label={`Remove ${item.name}`}
                      data-testid={`summary-remove-${item.id}`}
                      onClick={() => onRemove(item.id)}
                      className="mt-0.5 text-[#B9B2A4] transition-colors hover:text-[#1C1C1C]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

// ---------------- DESKTOP STICKY PANEL ----------------
export const SummaryPanel = ({ selectedIds, styles, total, onContinue, onRemove }) => {
  const empty = selectedIds.length === 0;
  return (
    <aside
      className="sticky top-24 border border-[#E8E3D9] bg-white/95 p-8"
      data-testid="summary-panel-desktop"
    >
      <span className="absolute inset-x-0 top-0 h-[2px] bg-[#C6A56B]" />
      <p className="font-sans text-[11px] uppercase tracking-[0.35em] text-[#111111]">
        Your Story
      </p>

      <div className="mt-6 max-h-[42vh] overflow-y-auto pr-1">
        <SummaryLines selectedIds={selectedIds} styles={styles} onRemove={onRemove} />
      </div>

      <div className="mt-8 border-t border-[#E8E3D9] pt-6">
        <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A]">
          Estimasi Biaya
        </p>
        <AnimatedRupiah
          value={total}
          testId="summary-total-desktop"
          className="mt-2 block font-serif text-4xl text-[#1C1C1C]"
        />
      </div>

      <button
        type="button"
        data-testid="summary-continue-desktop"
        disabled={empty}
        onClick={onContinue}
        className={cn(
          "group mt-6 flex w-full items-center justify-center gap-3 px-6 py-4 font-sans text-sm uppercase tracking-widest transition-colors duration-300",
          empty
            ? "cursor-not-allowed bg-[#E8E3D9] text-[#B9B2A4]"
            : "bg-[#111111] text-white hover:bg-[#C6A56B] hover:text-[#111111]"
        )}
      >
        Continue
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      {empty && (
        <p className="mt-3 text-center font-sans text-xs text-[#B9B2A4]">
          Pilih minimal satu layanan untuk lanjut.
        </p>
      )}
    </aside>
  );
};

// ---------------- MOBILE FIXED BOTTOM BAR ----------------
export const MobileSummaryBar = ({ selectedIds, styles, total, onContinue, onRemove }) => {
  const [open, setOpen] = useState(false);
  const empty = selectedIds.length === 0;
  const count = selectedIds.length;

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[75vh] overflow-y-auto rounded-t-2xl bg-white p-6 lg:hidden"
              data-testid="summary-sheet-mobile"
            >
              <div className="mb-5 flex items-center justify-between">
                <p className="font-sans text-[11px] uppercase tracking-[0.35em] text-[#1C1C1C]">
                  Your Story
                </p>
                <button
                  type="button"
                  aria-label="Close summary"
                  onClick={() => setOpen(false)}
                  data-testid="summary-sheet-close"
                >
                  <X className="h-5 w-5 text-[#8A8A8A]" />
                </button>
              </div>
              <SummaryLines selectedIds={selectedIds} styles={styles} onRemove={onRemove} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-[#E8E3D9] bg-white/95 px-4 py-3 backdrop-blur lg:hidden"
        data-testid="summary-bar-mobile"
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => !empty && setOpen((v) => !v)}
            className="flex flex-1 flex-col items-start"
            data-testid="summary-bar-toggle"
          >
            <span className="flex items-center gap-1 font-sans text-[10px] uppercase tracking-[0.25em] text-[#8A8A8A]">
              Estimasi Biaya
              {!empty && <ChevronUp className="h-3 w-3" />}
            </span>
            <AnimatedRupiah
              value={total}
              testId="summary-total-mobile"
              className="font-serif text-2xl leading-tight text-[#1C1C1C]"
            />
            {!empty && (
              <span className="font-sans text-[10px] text-[#B9B2A4]">
                {count} layanan dipilih · ketuk untuk lihat
              </span>
            )}
          </button>

          <button
            type="button"
            data-testid="summary-continue-mobile"
            disabled={empty}
            onClick={() => {
              setOpen(false);
              onContinue();
            }}
            className={cn(
              "flex items-center gap-2 px-6 py-3.5 font-sans text-xs uppercase tracking-widest transition-colors",
              empty
                ? "cursor-not-allowed bg-[#E8E3D9] text-[#B9B2A4]"
                : "bg-[#1C1C1C] text-white"
            )}
          >
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
};
